import { useEffect, useRef, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
// @ts-ignore
import ru from 'ckeditor5/translations/ru.js'

import {
  AccessibilityHelp,
  Alignment,
  AutoImage,
  Autosave,
  BlockQuote,
  Bold,
  ClassicEditor,
  CloudServices,
  Code,
  CodeBlock,
  EditorConfig,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  SourceEditing,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  Underline,
  Undo
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { Box } from "@mui/material";
import { IFRAME_TEMPLATES } from "constants/index";
import ImageUploadAdapterPlugin from "./ImageUploadAdapter";

type RichEditorProps = {
  onChange: (data: string) => void;
  value: string;
  error: any;
  disabled?: boolean;
  placeholder?: string;
};

const RichEditor = ({ onChange, value, error, disabled, placeholder }: RichEditorProps) => {
  const editorContainerRef = useRef(null);
  const editorRef = useRef(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  useEffect(() => {
    setIsLayoutReady(true);

    return () => setIsLayoutReady(false);
  }, []);

  const editorConfig: EditorConfig = {
    toolbar: {
      // оставить только базовое форматирование (жирный, курсив, списки, цитата, ссылка и т.д.),
      items: [
        'undo', 'redo',
        '|',
        'bold', 'italic', 'underline',
        '|',
        'link',
        '|',
        'bulletedList', 'numberedList',
        '|',
        'blockQuote'
      ],

      // items: [
      //   'undo',
      //   'redo',
      //   '|',
      //   'sourceEditing',
      //   '|',
      //   'heading',
      //   '|',
      //   'fontSize',
      //   'fontFamily',
      //   'fontColor',
      //   'fontBackgroundColor',
      //   '|',
      //   'bold',
      //   'italic',
      //   'underline',
      //   'strikethrough',
      //   'subscript',
      //   'superscript',
      //   'code',
      //   '|',
      //   'horizontalLine',
      //   'link',
      //   'insertImage',
      //   'mediaEmbed',
      //   'insertTable',
      //   'highlight',
      //   'blockQuote',
      //   'codeBlock',
      //   '|',
      //   'alignment',
      //   '|',
      //   'bulletedList',
      //   'numberedList',
      //   'outdent',
      //   'indent'
      // ],
      shouldNotGroupWhenFull: false
    },
    plugins: [
      AccessibilityHelp,
      Alignment,
      AutoImage,
      Autosave,
      // Base64UploadAdapter,
      BlockQuote,
      Bold,
      CloudServices,
      Code,
      CodeBlock,
      Essentials,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HorizontalLine,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageInsert,
      ImageInsertViaUrl,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      LinkImage,
      List,
      MediaEmbed,
      Paragraph,
      PasteFromOffice,
      SelectAll,
      SourceEditing,
      Strikethrough,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      Underline,
      Undo,
      ImageUploadAdapterPlugin
    ],
    mediaEmbed: {
      extraProviders: IFRAME_TEMPLATES,
      removeProviders: ['youtube']
    },
    fontFamily: {
      supportAllValues: true
    },
    fontSize: {
      options: [10, 12, 14, 'default', 18, 20, 22],
      supportAllValues: true
    },
    htmlSupport: {
      allow: [
        {
          name: /^.*$/,
          styles: true,
          attributes: true,
          classes: true
        }
      ]
    },
    translations: [ru],
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph'
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1'
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2'
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3'
        },
        {
          model: 'heading4',
          view: 'h4',
          title: 'Heading 4',
          class: 'ck-heading_heading4'
        },
        {
          model: 'heading5',
          view: 'h5',
          title: 'Heading 5',
          class: 'ck-heading_heading5'
        },
        {
          model: 'heading6',
          view: 'h6',
          title: 'Heading 6',
          class: 'ck-heading_heading6'
        }
      ]
    },
    image: {
      toolbar: [
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'imageStyle:inline',
        'imageStyle:wrapText',
        'imageStyle:breakText',
        '|',
        'resizeImage'
      ]
    },
    initialData: value,
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file'
          }
        }
      }
    },
    placeholder,
    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
    }
  };

  return (
    <div>
      <div className="main-container">
        <Box
          sx={{
            lineHeight: 1.6,
            wordBreak: 'break-word',
            '& .ck.ck-editor__main>.ck-editor__editable': {
              minHeight: 300,
              borderColor: error && 'error.main',
            },
          }}
          className="editor-container editor-container_classic-editor" ref={editorContainerRef}>
          <div className="editor-container__editor">
            <div ref={editorRef}>
              {isLayoutReady &&
                  <CKEditor
                      data={value}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        onChange(data);
                      }}
                      onReady={(editor) => {
                        const sourceEditing = editor.plugins.get('SourceEditing');

                        sourceEditing.on("change:isSourceEditingMode", (_eventInfo: unknown, _name: string, value: boolean) => {
                          if (value) {
                            const sourceEditingTextarea = editor.editing.view.getDomRoot()?.nextSibling?.firstChild;

                            if (!sourceEditingTextarea) {
                              throw new Error(`sourceEditingTextarea: ${sourceEditingTextarea}`);
                            }
                            sourceEditingTextarea.addEventListener('input', () => {
                              sourceEditing.updateEditorData();
                            });
                          }
                        });
                      }}
                      disabled={disabled}
                      editor={ClassicEditor}
                      config={editorConfig}/>
              }
            </div>
          </div>
        </Box>
      </div>
    </div>
  );
}

export default RichEditor;
